import { DateTime } from "luxon"
import { parseApiCalendarDate } from "./HelperDate"
import { IOrder } from "./InterfacesAndTypes"

export function applySearchExclusionRules(row, coalescedRecord, rawRecords: any[]) {
	if (row.RECORD_ACTION === 'CSR' || row.RECORD_ACTION === 'CSL' || row.RECORD_ACTION === 'PSL' || row.RECORD_ACTION === 'PSR') {
		coalescedRecord.__recordIsSealed = true
		coalescedRecord.__recordSealedReason = `ACTION_SEQ ${row.ACTION_SEQ} is ${row.RECORD_ACTION}`
	}
}

export function findIndexOfFirstIncludedValue(arrayOfThings: string[], possibleValues: string[]) {
	return arrayOfThings.indexOf(
		possibleValues.find((candidate) => {
			return arrayOfThings.includes(candidate.toLowerCase())
		}) || "notfound")
}

export function isStringEmpty(str: string): boolean {
	return (!str || str.length === 0)
}

export function isStringEmptyOrWhitespace(str: string): boolean {
	return (isStringEmpty(str) || str.trim().length === 0)
}

export function isStringDictionaryEmptyOrWhitespace(obj: Object): boolean {
	return (!obj || !Object.values(obj) || Object.values(obj).filter((val) => {
		return typeof val === 'string' && !isStringEmptyOrWhitespace(<string>val)
	}).length === 0)
}

export function stripNull(obj) {
	return Object.entries(obj)
		.filter(([_, v]) => v !== null)
		.reduce((acc, [k, v]) => ({ ...acc, [k]: v}), {})
}

export function stripUndefined(obj) {
	return Object.entries(obj)
		.filter(([_, v]) => v !== undefined)
		.reduce((acc, [k, v]) => ({ ...acc, [k]: v}), {})
}

export function stripNullish(obj) {
	return Object.entries(obj)
		.filter(([_, v]) => v != null)
		.reduce((acc, [k, v]) => ({ ...acc, [k]: v}), {})
}

export function stripNullAndUndefined(obj) {
	return stripUndefined(stripNull(obj))
}

export function setPageTitle(title: string, subtitle?: string) {
	document.title = `${title}${subtitle ? ' - ' + subtitle : ''} | Wil-Max Case Search`
}

export function filterOrderHistory(orders: any[], options: {
	startDate?: Date | null;
	endDate?: Date | null;
	dateProperty?: string;
	statuses?: string[];
	statusProperty?: string;
} = {}): any[] {
	if (!orders || !Array.isArray(orders)) {
		return [];
	}

	const {
		startDate,
		endDate,
		dateProperty = 'createdAt',
		statuses = [],
		statusProperty = 'status',
	} = options;
	let filtered = [...orders];

	filtered.sort((firstOrder, secondOrder) => {
		const firstOrderDate = parseApiCalendarDate(String(firstOrder[dateProperty] ?? ""))
		const secondOrderDate = parseApiCalendarDate(String(secondOrder[dateProperty] ?? ""))
		const firstMs = firstOrderDate?.toMillis() ?? 0
		const secondMs = secondOrderDate?.toMillis() ?? 0
		return secondMs - firstMs
	})

	if (startDate) {
		const startLuxon = DateTime.fromJSDate(startDate, { zone: "local" }).startOf("day")
		filtered = filtered.filter((order) => {
			const orderDate = parseApiCalendarDate(String(order[dateProperty] ?? ""))
			if (!orderDate) {
				return false
			}
			return orderDate >= startLuxon
		})
	}

	if (endDate) {
		const endLuxon = DateTime.fromJSDate(endDate, { zone: "local" }).endOf("day")
		filtered = filtered.filter((order) => {
			const orderDate = parseApiCalendarDate(String(order[dateProperty] ?? ""))
			if (!orderDate) {
				return false
			}
			return orderDate <= endLuxon
		})
	}

	if (statuses && statuses.length > 0) {
		filtered = filtered.filter(order => {
			if (order.orderRows && Array.isArray(order.orderRows)) {
				return statuses.some(status => {
					switch (status.toLowerCase()) {
						case 'sent':
							return order.orderRows.some((row: { courtsSent: any }) => row.courtsSent);
						case 'received':
							return order.orderRows.some((row: { courtsReceived: any }) => row.courtsReceived);
						case 'pending':
							return order.orderRows.some((row: { courtsSent: any }) => !row.courtsSent);
						case 'waiting':
							return order.orderRows.some((row: { courtsSent: any; courtsReceived: any }) => row.courtsSent && !row.courtsReceived);
						case 'completed':
							return order.orderRows.every((row: { isClosed: any }) => row.isClosed);
						case 'incomplete':
							return !order.orderRows.every((row: { isClosed: any }) => row.isClosed);
						default:
							const orderStatus = order[statusProperty]?.toLowerCase();
							return status.toLowerCase() === orderStatus;
					}
				});
			} else {
				const orderStatus = order[statusProperty]?.toLowerCase();
				return statuses.some(status => status.toLowerCase() === orderStatus);
			}
		});
	}

	return filtered;
}

export function isOrderRowClosed(row: { isClosed: boolean; closedAt: string | null }): boolean {
	return row.isClosed === true && row.closedAt !== null;
}

// Special flags: free-form row markers stored as a comma-separated string. Icon
// name doubles as the stored value; colors reuse the app's Bulma/Buefy palette.
export const SPECIAL_FLAG_ORDER = ['star', 'flag', 'alert'] as const;
export type SpecialFlag = typeof SPECIAL_FLAG_ORDER[number];

export const SPECIAL_FLAG_COLORS: Record<SpecialFlag, string> = {
	star: '#ffdd57',  // gold (is-warning)
	flag: '#3273dc',  // blue (Bulma link, used elsewhere in the app)
	alert: '#ff3860', // red (is-danger)
};

// Parse a CSV of flags into the canonical, de-duped, validated ordered list.
export function parseSpecialFlags(csv?: string | null): SpecialFlag[] {
	if (!csv) {
		return [];
	}
	const present = new Set(csv.split(',').map((s) => s.trim()));
	return SPECIAL_FLAG_ORDER.filter((flag) => present.has(flag));
}

// Roll the flags up to the order level. When rows are loaded (order detail) union
// their flags; otherwise fall back to the aggregated string from get-order-list.
export function rollupSpecialFlags(order: IOrder): SpecialFlag[] {
	const rows = order.orderRows ?? [];
	if (rows.length) {
		const present = new Set<SpecialFlag>();
		for (const row of rows) {
			for (const flag of parseSpecialFlags(row.specialFlags)) {
				present.add(flag);
			}
		}
		return SPECIAL_FLAG_ORDER.filter((flag) => present.has(flag));
	}
	return parseSpecialFlags(order.specialFlagsRollup);
}

export type OrderCompletionState = 'completed' | 'partial' | 'incomplete';

export function getOrderCompletionState(order: IOrder): OrderCompletionState {
	const rows = order.orderRows ?? [];
	if (rows.length === 0) { // this usually means we've loaded the order in memory, but not the rows, due to a get-order-list call
		if (order.closedPercentage === 1) {
			return 'completed';
		}
		if (order.closedPercentage > 0 && order.closedPercentage < 1) {
			return 'partial';
		}
		return 'incomplete';
	}
	const closedCount = rows.filter(isOrderRowClosed).length; // this means we've at some point called get-order for this specific order, so we have rows to check
	if (closedCount === rows.length) {
		return 'completed';
	}
	if (closedCount > 0) {
		return 'partial';
	}
	return 'incomplete';
}

export function getDateGroupCompletionStatus(orders: IOrder[]) {
	const states = orders.map(getOrderCompletionState);
	const totalOrders = orders.length;
	const completedOrders = states.filter((s) => s === 'completed').length;

	let status: 'empty' | 'all-completed' | 'partially-completed' | 'none-completed';
	if (totalOrders === 0) {
		status = 'empty';
	} else if (states.every((s) => s === 'completed')) {
		status = 'all-completed';
	} else if (states.every((s) => s === 'incomplete')) {
		status = 'none-completed';
	} else {
		status = 'partially-completed';
	}

	return { status, totalOrders, completedOrders };
}

export function groupOrdersByDate(orders: any[], dateProperty: string = 'date') {
	if (!orders || !Array.isArray(orders)) {
		return {};
	}

	const grouped = orders.reduce((acc, order) => {
		const parsed = parseApiCalendarDate(String(order[dateProperty] ?? ""))
		const dateStr = parsed?.toFormat("yyyy-MM-dd") ?? "unknown"

		if (!acc[dateStr]) {
			acc[dateStr] = [];
		}
		acc[dateStr].push(order);
		return acc;
	}, {});

	return Object.keys(grouped)
		.sort((a, b) => {
			const aDt = DateTime.fromFormat(a, "yyyy-MM-dd", { zone: "local" })
			const bDt = DateTime.fromFormat(b, "yyyy-MM-dd", { zone: "local" })
			const aMs = (a === "unknown" || !aDt.isValid) ? 0 : aDt.toMillis()
			const bMs = (b === "unknown" || !bDt.isValid) ? 0 : bDt.toMillis()
			return bMs - aMs
		})
		.map((dateStr) => ({
			date: dateStr,
			orders: grouped[dateStr],
		}));
}

